import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { LocationGroup } from '@core/interfaces';
import { fuseAnimations } from '@fuse/animations';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { merge, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DialogLocationsGroupsComponent } from './dialogs/dialog-locations-groups.component';
import { FiltersComponent } from './filters/filters.component';
import { LocationsGroupsService } from './services/locations-groups-resolver.service';

import { AuthenticationService } from '@core/authentication/authentication.service';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

@Component({
  selector: 'locations-groups',
  templateUrl: './locations-groups.component.html',
  styleUrls: ['./locations-groups.component.scss'],
  animations: fuseAnimations,
  encapsulation: ViewEncapsulation.None
})
export class LocationsGroupsComponent implements OnInit {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(FiltersComponent, { static: true }) filterLocationsGroupsComponent: FiltersComponent;
  @ViewChild('datatable', { read: ElementRef, static: true }) datatable: ElementRef<HTMLDivElement>;

  public displayedColumns: Array<String> = ['name', 'description', 'createdAt', 'edit'];
  public locationsGroups: Array<LocationGroup> = [];
  public totalCount: Number;
  public filtered: boolean;
  public loading = true;

  locationsGroupsReload = new Subject<any>();
  subscriptions = new Subject<void>();

  public hasEditLocationGroupPermission = this.authenticationService.hasPermission('locations-groups-edit');
  public hasCreateLocationGroupPermission = this.authenticationService.hasPermission('locations-groups-create');

  constructor(
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private locationsGroupsService: LocationsGroupsService,
    private authenticationService: AuthenticationService,
    private fuseSidebarService: FuseSidebarService,
    private matDialog: MatDialog
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  ngOnInit(): void {
    this.sort.active = 'name';
    this.sort.direction = 'asc';

    merge(this.sort.sortChange,
      this.paginator.page,
      this.filterLocationsGroupsComponent.filterChanged.pipe(
        tap(() => this.paginator.firstPage())
      ),
      this.locationsGroupsReload
    )
      .pipe(
        debounceTime(500),
        takeUntil(this.subscriptions),
        switchMap(() => {
          this.loading = true;

          this.sort.direction = this.sort.direction || 'asc';

          const options = {
            orderBy: `${this.sort.active}_${this.sort.direction}`,
            offset: this.paginator.pageIndex * this.paginator.pageSize,
            limit: this.paginator.pageSize
          };

          return this.locationsGroupsService.getLocationsGroups({
            where: this.filterLocationsGroupsComponent.filterChanged.value,
            options
          });
        })
      )
      .subscribe((result) => {
        this.datatable.nativeElement.scrollTo({
          top: 0,
        });

        this.locationsGroups = result.items;
        this.totalCount = result.totalCount;
        this.filtered = result.filtered;
        this.loading = false;
      });
  }

  reload(): void {
    this.locationsGroupsReload.next();
  }

  toggleSidebarOpen(name): void {
    this.fuseSidebarService.getSidebar(name).toggleOpen();
  }

  async openDialog(locationGroupId?: string): Promise<void> {
    const locationGroup = await this.locationsGroupsService.getLocationGroup(locationGroupId);
    const dialog = this.matDialog.open(DialogLocationsGroupsComponent, {
      panelClass: ['dialog-locations-groups'],
      data: {
        locationGroup: locationGroup || {},
        isViewMode: locationGroupId && !this.hasEditLocationGroupPermission
      }
    });

    const reload = await dialog.afterClosed().toPromise();
    if (reload) {
      this.reload();
    }
  }

  clearFilters(): void {
    this.filtered = false;
    this.filterLocationsGroupsComponent.clearFilters();
  }
}
